import { buildHref, type GameAddress } from "@softmax/web-addresses";
import type { Schemas } from "@softmax/observatory-client/types";

export type GamePageContext = {
  readonly viewer: Schemas["WhoAmIResponse"] | null;
  readonly address: GameAddress;
  readonly corpusSlug: string;
  readonly publicPath: string;
  readonly gameId: string | null;
  readonly hasVisibleLeagues: boolean;
  readonly league: {
    readonly id: string;
    readonly requestedSlug: string | null;
    readonly public: boolean;
  } | null;
  readonly presentation: "theater" | "home" | "desk";
} & (
  | {
      readonly mode: "league";
      readonly coworldId: string | null;
      readonly coworldName: string | null;
    }
  | {
      readonly mode: "upload";
      readonly coworldId: string;
      readonly coworldName: string;
    }
);

export type GameNavigation =
  | {
      readonly kind: "path";
      readonly pathname: string;
      readonly status: 307 | 308;
    }
  | { readonly kind: "sign-in" }
  | { readonly kind: "lobby" };

export type GameAddressResolution =
  | { readonly kind: "resolved"; readonly context: GamePageContext }
  | {
      readonly kind: "navigate";
      readonly navigation: GameNavigation;
      readonly viewer: GamePageContext["viewer"];
    }
  | { readonly kind: "not-found" | "unavailable" | "unmatched" | "invalid" };

export type GameContextRead =
  | { readonly kind: "ready"; readonly game: GamePageContext }
  | {
      readonly kind: "navigate";
      readonly navigation: GameNavigation;
      readonly viewer: GamePageContext["viewer"];
    };

export const gameTheaterQueryKeys = [
  "e",
  "view",
  "league",
  "kiosk",
  "version",
] as const;

export function gameResourceSearch(search: Record<string, unknown>): string {
  const parameters = new URLSearchParams();

  for (const key of ["version", "league"]) {
    const value = Array.isArray(search[key]) ? search[key][0] : search[key];

    if (typeof value === "string") parameters.set(key, value);
  }

  const view = Array.isArray(search.view) ? search.view[0] : search.view;

  if (view === "lobby") parameters.set("view", "lobby");
  else if (gameTheaterQueryKeys.some((key) => Object.hasOwn(search, key)))
    parameters.set("view", "theater");

  return parameters.toString();
}

export function gameResourceHref(href: string): string {
  const url = new URL(href, "https://softmax.invalid");
  const search = gameResourceSearch(
    Object.fromEntries(
      gameTheaterQueryKeys
        .filter((key) => url.searchParams.has(key))
        .map((key) => [key, url.searchParams.get(key)]),
    ),
  );
  return `${url.pathname}${search ? `?${search}` : ""}`;
}

export function gameNavigationHref({
  navigation,
  href,
}: {
  readonly navigation: GameNavigation;
  readonly href: string;
}): string {
  const url = new URL(href, "https://softmax.invalid");

  if (navigation.kind === "sign-in")
    return buildHref({
      name: "signIn",
      search: new URLSearchParams({
        redirect_url: `${url.pathname}${url.search}${url.hash}`,
      }),
    });

  if (navigation.kind === "lobby") {
    url.searchParams.delete("view");
    return buildHref({
      name: "browse",
      search: url.searchParams,
      hash: url.hash,
    });
  }

  return `${navigation.pathname}${url.search}${url.hash}`;
}
